import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const TemplateDetails = ({ show, onHide, template }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [calculatedSummary, setCalculatedSummary] = useState(null);
  const [calculatedComponents, setCalculatedComponents] = useState([]);
  const [formulaVariables, setFormulaVariables] = useState({
    salary_value: 30000,
    basic: 30000,
    proration_ratio: 1.0,
    present_days: 26,
    effective_days: 26,
    pf_percentage: 12,
    esi_percentage: 1.75
  });

  const API_BASE = process.env.REACT_APP_API_BASE;

  // Safe math expression evaluator
  const evaluateMathExpression = useCallback((expression) => {
    try {
      // Remove any remaining unsafe characters
      const sanitized = expression.replace(/[^0-9+\-*/().,]/g, '');

      // Use a safe evaluation method
      const tokens = sanitized.match(/(\d+(?:\.\d+)?|[+\-*/()])/g);
      if (!tokens) return 0;

      // Simple expression evaluator for basic arithmetic
      const evaluate = (tokens) => {
        const values = [];
        const ops = [];

        const precedence = {
          '+': 1,
          '-': 1,
          '*': 2,
          '/': 2
        };

        const applyOp = (op, b, a) => {
          switch (op) {
            case '+': return a + b;
            case '-': return a - b;
            case '*': return a * b;
            case '/': return b !== 0 ? a / b : 0;
            default: return 0;
          }
        };

        for (let i = 0; i < tokens.length; i++) {
          const token = tokens[i];

          if (!isNaN(token)) {
            values.push(parseFloat(token));
          } else if (token === '(') {
            ops.push(token);
          } else if (token === ')') {
            while (ops.length && ops[ops.length - 1] !== '(') {
              values.push(applyOp(ops.pop(), values.pop(), values.pop()));
            }
            ops.pop();
          } else if (['+', '-', '*', '/'].includes(token)) {
            while (ops.length && precedence[ops[ops.length - 1]] >= precedence[token]) {
              values.push(applyOp(ops.pop(), values.pop(), values.pop()));
            }
            ops.push(token);
          }
        }

        while (ops.length) {
          values.push(applyOp(ops.pop(), values.pop(), values.pop()));
        }

        return values.length ? values.pop() : 0;
      };

      const result = evaluate(tokens);
      return isNaN(result) ? 0 : parseFloat(result.toFixed(2));
    } catch (error) {
      console.error('Math expression evaluation error:', error);
      return 0;
    }
  }, []);

  const evaluateFormulaWithComponents = useCallback((formula, components, variables) => {
    try {
      // Create a map of component values
      const componentValues = {};
      components.forEach(comp => {
        componentValues[comp.component_name] = comp.calculated_amount || 0;
      });

      // Replace component names with their values
      let evalFormula = formula;
      Object.keys(componentValues).forEach(name => {
        const regex = new RegExp(`\\b${name}\\b`, 'gi');
        evalFormula = evalFormula.replace(regex, componentValues[name]);
      });

      // Replace variable names with their values
      Object.keys(variables).forEach(varName => {
        const regex = new RegExp(`\\b${varName}\\b`, 'gi');
        evalFormula = evalFormula.replace(regex, variables[varName]);
      });

      // Handle percentage signs
      evalFormula = evalFormula.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)');

      // Handle basic arithmetic functions
      evalFormula = evalFormula.replace(/min\(/gi, 'Math.min(');
      evalFormula = evalFormula.replace(/max\(/gi, 'Math.max(');
      evalFormula = evalFormula.replace(/abs\(/gi, 'Math.abs(');
      evalFormula = evalFormula.replace(/round\(/gi, 'Math.round(');
      evalFormula = evalFormula.replace(/sqrt\(/gi, 'Math.sqrt(');
      evalFormula = evalFormula.replace(/pow\(/gi, 'Math.pow(');

      // Remove any unsafe characters
      evalFormula = evalFormula.replace(/[^0-9+\-*/().,]/g, '');

      // Safe evaluation using math expression parser
      return evaluateMathExpression(evalFormula);
    } catch (error) {
      console.error('Formula evaluation error:', error, 'Formula:', formula);
      return 0;
    }
  }, [evaluateMathExpression]);

  const calculateClientSideSummary = useCallback((templateData) => {
    if (!templateData || !templateData.components) return;

    const components = templateData.components || [];
    const calculatedComps = [];
    let earnings = 0;
    let deductions = 0;

    // First pass: calculate fixed and percentage components
    components.forEach(component => {
      let amount = 0;
      const value = parseFloat(component.value) || 0;
      const calcType = component.calculation_type || 'Fixed';

      switch (calcType) {
        case 'Fixed':
          amount = value;
          break;
        case 'Percentage':
          amount = formulaVariables.salary_value * (value / 100);
          break;
        case 'Formula':
          // Will calculate in second pass
          amount = 0;
          break;
        default:
          amount = value;
      }

      calculatedComps.push({
        ...component,
        calculated_amount: amount
      });
    });

    // Second pass: calculate formula components
    calculatedComps.forEach((component, index) => {
      if (component.calculation_type === 'Formula') {
        const formulaResult = evaluateFormulaWithComponents(
          component.value,
          calculatedComps,
          formulaVariables
        );
        calculatedComps[index].calculated_amount = formulaResult;
      }

      // Add to summary
      const amount = calculatedComps[index].calculated_amount || 0;
      if (component.component_type === 'Earning' || component.component_type === 'Earnings') {
        earnings += amount;
      } else if (component.component_type === 'Deduction') {
        deductions += amount;
      }
    });

    setCalculatedComponents(calculatedComps);
    setCalculatedSummary({
      earnings: earnings.toFixed(2),
      deductions: deductions.toFixed(2),
      net: (earnings - deductions).toFixed(2)
    });
  }, [formulaVariables, evaluateFormulaWithComponents]);

  const calculateTemplateSummary = useCallback(async (templateData) => {
    try {
      const token = localStorage.getItem('authToken');

      // Call backend API to calculate template components
      const response = await axios.post(`${API_BASE}/calculate_template_summary.php`,
        {
          template_id: template.template_id,
          salary_value: formulaVariables.salary_value,
          proration_ratio: formulaVariables.proration_ratio
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setCalculatedComponents(response.data.components || []);
        setCalculatedSummary(response.data.summary || {
          earnings: '0.00',
          deductions: '0.00',
          net: '0.00'
        });
      } else {
        // Fallback to client-side calculation
        calculateClientSideSummary(templateData);
      }
    } catch (error) {
      console.error('Error calculating template summary:', error);
      // Fallback to client-side calculation
      calculateClientSideSummary(templateData);
    }
  }, [API_BASE, template.template_id, formulaVariables, calculateClientSideSummary]);

  const fetchTemplateDetails = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      // Fetch template details
      const response = await axios.get(`${API_BASE}/get_template_details.php`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { template_id: template.template_id }
      });

      if (response.data.success) {
        setDetails(response.data);

        // Calculate template summary
        calculateTemplateSummary(response.data);
      } else {
        setError(response.data.error || 'Failed to fetch template details');
      }
    } catch (error) {
      setError('Failed to fetch template details');
      console.error('Error fetching template details:', error);
    } finally {
      setLoading(false);
    }
  }, [template, API_BASE, calculateTemplateSummary]);

  useEffect(() => {
    if (show && template) {
      fetchTemplateDetails();
    }
  }, [show, template, fetchTemplateDetails]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleVariableChange = (variable, value) => {
    setFormulaVariables(prev => ({
      ...prev,
      [variable]: parseFloat(value) || 0
    }));
  };

  const recalculateSummary = () => {
    if (details) {
      calculateClientSideSummary(details);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal show d-block" style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1055,
        overflowY: 'auto',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        padding: '2rem 0'
      }}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderBottom: 'none',
              padding: '1rem 1.5rem'
            }}>
              <h5 className="modal-title text-white mb-0">
                <i className="bi bi-file-earmark-text me-2"></i>
                Template Details: {template.template_name}
              </h5>
              <div className="d-flex align-items-center">
                <button type="button" className="btn-close btn-close-white" onClick={onHide}></button>
              </div>
            </div>

            <div className="modal-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading template details...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </div>
              ) : details ? (
                <>
                  {/* Template Information */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card border-0 bg-light">
                        <div className="card-body">
                          <h6 className="fw-bold text-primary mb-3">
                            <i className="bi bi-info-circle me-2"></i>
                            Template Information
                          </h6>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="mb-2">
                                <small className="text-muted d-block">Template Name</small>
                                <strong>{details.template?.template_name || template.template_name}</strong>
                              </div>
                              <div className="mb-2">
                                <small className="text-muted d-block">Template ID</small>
                                <strong>{details.template?.template_id || template.template_id}</strong>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-2">
                                <small className="text-muted d-block">Description</small>
                                <strong>{details.template?.description || template.description || 'No description'}</strong>
                              </div>
                              <div className="mb-2">
                                <small className="text-muted d-block">Total Components</small>
                                <strong>{details.components?.length || 0}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Formula Variables */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card border-primary">
                        <div className="card-header bg-primary text-white">
                          <h6 className="mb-0">
                            <i className="bi bi-sliders me-2"></i>
                            Formula Variables (For Calculation)
                          </h6>
                        </div>
                        <div className="card-body">
                          <div className="row g-3">
                            <div className="col-md-3">
                              <label className="form-label">Basic Salary (₹)</label>
                              <input
                                type="number"
                                className="form-control"
                                value={formulaVariables.salary_value}
                                onChange={(e) => handleVariableChange('salary_value', e.target.value)}
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">Proration Ratio</label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={formulaVariables.proration_ratio}
                                onChange={(e) => handleVariableChange('proration_ratio', e.target.value)}
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">Present Days</label>
                              <input
                                type="number"
                                className="form-control"
                                value={formulaVariables.present_days}
                                onChange={(e) => handleVariableChange('present_days', e.target.value)}
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">PF %</label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={formulaVariables.pf_percentage}
                                onChange={(e) => handleVariableChange('pf_percentage', e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="mt-3">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={recalculateSummary}
                            >
                              <i className="bi bi-calculator me-1"></i>
                              Recalculate
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Template Components */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <h6 className="fw-bold text-primary mb-3">
                        <i className="bi bi-grid-3x3-gap me-2"></i>
                        Template Components & Calculations
                      </h6>

                      {details.components && details.components.length > 0 ? (
                        <div className="card border-0 shadow-sm">
                          <div className="card-body p-0">
                            <div className="table-responsive">
                              <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light">
                                  <tr>
                                    <th>#</th>
                                    <th>Component Name</th>
                                    <th>Type</th>
                                    <th>Calculation Type</th>
                                    <th>Value/Formula</th>
                                    <th className="text-end">Calculated Amount (₹)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {details.components.map((comp, index) => {
                                    const calculatedComp = calculatedComponents.find(
                                      c => c.component_id === comp.component_id
                                    );
                                    const amount = calculatedComp?.calculated_amount ||
                                      calculateComponentAmount(comp, formulaVariables);

                                    return (
                                      <tr key={comp.template_detail_id || index}>
                                        <td>
                                          <span className="badge bg-secondary">{comp.component_order || (index + 1)}</span>
                                        </td>
                                        <td>
                                          <div className="d-flex align-items-center">
                                            <div className={`component-icon ${(comp.component_type === 'Earning' || comp.component_type === 'Earnings') ? 'bg-success' : 'bg-danger'} text-white rounded-circle d-flex align-items-center justify-content-center me-2`}
                                              style={{ width: '30px', height: '30px' }}
                                            >
                                              <i className={`bi ${(comp.component_type === 'Earning' || comp.component_type === 'Earnings') ? 'bi-plus' : 'bi-dash'}`}></i>
                                            </div>
                                            <div>
                                              <div className="fw-semibold">{comp.component_name}</div>
                                              <small className="text-muted">
                                                {comp.component_type}
                                              </small>
                                            </div>
                                          </div>
                                        </td>
                                        <td>
                                          <span className={`badge ${(comp.component_type === 'Earning' || comp.component_type === 'Earnings') ? 'bg-success' : 'bg-danger'}`}>
                                            {comp.component_type}
                                          </span>
                                        </td>
                                        <td>
                                          <span className={`badge ${comp.calculation_type === 'Formula' ? 'bg-warning' :
                                            comp.calculation_type === 'Percentage' ? 'bg-info' : 'bg-secondary'
                                            }`}>
                                            {comp.calculation_type || 'Fixed'}
                                          </span>
                                        </td>
                                        <td>
                                          <div className="font-monospace small" title={comp.value}>
                                            {comp.calculation_type === 'Formula' ? (
                                              <span className="text-info">{comp.value}</span>
                                            ) : comp.value}
                                          </div>
                                        </td>
                                        <td className="text-end">
                                          <div className={`fw-bold ${amount >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {formatCurrency(amount)}
                                          </div>
                                          <small className="text-muted">
                                            {comp.calculation_type === 'Percentage' && `(${comp.value}% of ₹${formulaVariables.salary_value.toLocaleString()})`}
                                          </small>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="alert alert-info">
                          <i className="bi bi-info-circle me-2"></i>
                          No components found for this template.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Salary Summary */}
                  <div className="row">
                    <div className="col-12">
                      <h6 className="fw-bold text-primary mb-3">
                        <i className="bi bi-calculator me-2"></i>
                        Salary Summary
                      </h6>

                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <div className="card border-success border-2 h-100">
                            <div className="card-body text-center">
                              <h5 className="card-title text-success">
                                <i className="bi bi-arrow-up-circle me-2"></i>
                                Total Earnings
                              </h5>
                              <h2 className="fw-bold mt-3">
                                {calculatedSummary ? formatCurrency(calculatedSummary.earnings) : '₹0.00'}
                              </h2>
                              <div className="mt-2">
                                <small className="text-muted">
                                  {calculatedComponents.filter(c =>
                                    c.component_type === 'Earning' || c.component_type === 'Earnings'
                                  ).length} components
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="card border-danger border-2 h-100">
                            <div className="card-body text-center">
                              <h5 className="card-title text-danger">
                                <i className="bi bi-arrow-down-circle me-2"></i>
                                Total Deductions
                              </h5>
                              <h2 className="fw-bold mt-3">
                                {calculatedSummary ? formatCurrency(calculatedSummary.deductions) : '₹0.00'}
                              </h2>
                              <div className="mt-2">
                                <small className="text-muted">
                                  {calculatedComponents.filter(c =>
                                    c.component_type === 'Deduction'
                                  ).length} components
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="card border-primary border-2 h-100">
                            <div className="card-body text-center">
                              <h5 className="card-title text-primary">
                                <i className="bi bi-calculator me-2"></i>
                                Net Salary
                              </h5>
                              <h2 className="fw-bold mt-3">
                                {calculatedSummary ? formatCurrency(calculatedSummary.net) : '₹0.00'}
                              </h2>
                              <div className="mt-2">
                                <small className="text-muted">
                                  Take-home amount
                                </small>
                              </div>
                              {calculatedSummary && (
                                <div className="mt-3">
                                  <div className="progress" style={{ height: '8px' }}>
                                    <div
                                      className="progress-bar bg-success"
                                      style={{
                                        width: `${(calculatedSummary.earnings / formulaVariables.salary_value * 100).toFixed(1)}%`
                                      }}
                                    ></div>
                                  </div>
                                  <small className="text-muted">
                                    Earnings: {((calculatedSummary.earnings / formulaVariables.salary_value) * 100).toFixed(1)}% of Basic
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  No template details found.
                </div>
              )}
            </div>

            <div className="modal-footer" style={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
              borderTop: '1px solid rgba(102, 126, 234, 0.1)'
            }}>
              <button
                type="button"
                className="btn btn-info text-white me-auto"
                onClick={() => setShowHelpModal(true)}
                style={{
                  fontWeight: '500',
                  background: '#0dcaf0',
                  border: 'none',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#0bb5d4';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(13, 202, 240, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#0dcaf0';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <i className="bi bi-question-circle me-2"></i>
                Need Help?
              </button>
              <div className="text-muted small me-3">
                <i className="bi bi-info-circle me-1"></i>
                Calculations are based on the formula variables above. Actual payroll may vary.
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onHide}
              >
                <i className="bi bi-check-circle me-2"></i>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelpModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-question-circle me-2"></i>
                  Understanding Template Details
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowHelpModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {/* Help content */}
                <div className="alert alert-info">
                  <i className="bi bi-currency-rupee me-2"></i>
                  <strong>Note:</strong> All amounts are displayed in Indian Rupees (₹)
                </div>

                <h6 className="fw-bold mb-3">Formula Variables Explained</h6>
                <ul>
                  <li><strong>Basic Salary:</strong> The base salary used for percentage calculations</li>
                  <li><strong>Proration Ratio:</strong> Ratio of present days to working days (e.g., 0.8 for 80% attendance)</li>
                  <li><strong>Present Days:</strong> Number of days employee was present</li>
                  <li><strong>PF %:</strong> Provident Fund percentage (default: 12%)</li>
                </ul>

                <h6 className="fw-bold mb-3 mt-4">Calculation Types</h6>
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Description</th>
                        <th>Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><span className="badge bg-secondary">Fixed</span></td>
                        <td>Fixed amount regardless of other factors</td>
                        <td>₹5,000 (fixed allowance)</td>
                      </tr>
                      <tr>
                        <td><span className="badge bg-info">Percentage</span></td>
                        <td>Percentage of Basic Salary</td>
                        <td>50% of Basic = ₹15,000 (if Basic = ₹30,000)</td>
                      </tr>
                      <tr>
                        <td><span className="badge bg-warning">Formula</span></td>
                        <td>Calculated using mathematical formula</td>
                        <td>Basic * 0.5 + 1000</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowHelpModal(false)}
                >
                  <i className="bi bi-check-circle me-2"></i>
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Helper function to calculate component amount
const calculateComponentAmount = (component, variables) => {
  try {
    const value = parseFloat(component.value) || 0;
    const type = component.calculation_type || 'Fixed';

    switch (type) {
      case 'Fixed':
        return value;
      case 'Percentage':
        return variables.salary_value * (value / 100);
      case 'Formula':
        // Simple formula evaluation for display
        let formula = component.value;
        formula = formula.replace(/salary_value/gi, variables.salary_value);
        formula = formula.replace(/basic/gi, variables.salary_value);
        formula = formula.replace(/(\d+)%/g, '($1/100)');

        // Use safe evaluation instead of Function constructor
        const evaluateMathExpression = (expression) => {
          try {
            const sanitized = expression.replace(/[^0-9+\-*/().,]/g, '');
            const tokens = sanitized.match(/(\d+(?:\.\d+)?|[+\-*/()])/g);
            if (!tokens) return 0;

            const evaluate = (tokens) => {
              const values = [];
              const ops = [];

              const precedence = {
                '+': 1,
                '-': 1,
                '*': 2,
                '/': 2
              };

              const applyOp = (op, b, a) => {
                switch (op) {
                  case '+': return a + b;
                  case '-': return a - b;
                  case '*': return a * b;
                  case '/': return b !== 0 ? a / b : 0;
                  default: return 0;
                }
              };

              for (let i = 0; i < tokens.length; i++) {
                const token = tokens[i];

                if (!isNaN(token)) {
                  values.push(parseFloat(token));
                } else if (token === '(') {
                  ops.push(token);
                } else if (token === ')') {
                  while (ops.length && ops[ops.length - 1] !== '(') {
                    values.push(applyOp(ops.pop(), values.pop(), values.pop()));
                  }
                  ops.pop();
                } else if (['+', '-', '*', '/'].includes(token)) {
                  while (ops.length && precedence[ops[ops.length - 1]] >= precedence[token]) {
                    values.push(applyOp(ops.pop(), values.pop(), values.pop()));
                  }
                  ops.push(token);
                }
              }

              while (ops.length) {
                values.push(applyOp(ops.pop(), values.pop(), values.pop()));
              }

              return values.length ? values.pop() : 0;
            };

            const result = evaluate(tokens);
            return isNaN(result) ? 0 : parseFloat(result.toFixed(2));
          } catch (error) {
            console.error('Math expression evaluation error:', error);
            return 0;
          }
        };

        return evaluateMathExpression(formula);
      default:
        return value;
    }
  } catch (error) {
    console.error('Error calculating component amount:', error);
    return 0;
  }
};

export default TemplateDetails;